import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/dateFmt';
import {
  ArrowLeft,
  ChevronRight,
  FileImage,
  FileText,
  FilePlus2,
  Search,
  Users,
} from 'lucide-react';
import type { DocumentAttachedPayload, MobilisationEvent } from '@hcc/types';
import { hasOnboardingAccess } from '@hcc/types';
import { api } from '../../lib/api';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  usePeopleList,
  usePerson,
  usePersonEvents,
  useAppendEvent,
} from '../../hooks/usePeople';
import { useIntentKey } from '../../hooks/useIntentKey';
import { PersonAvatar } from '../../components/person/PersonAvatar';
import { AddDocumentModal } from '../../components/person/AddDocumentModal';
import { Button } from '../../components/ui/Button';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { ApiError } from '../../lib/api';

/**
 * Documents hub. Lands on a searchable list of attendees; clicking
 * one drops into their per-person documents view (thumbnails + Add
 * document). Replaces the handbook-signing flow — see CLAUDE.md 6.4.
 *
 * Image-only for v1: documents come from the device camera or photo
 * roll. PDFs and signed forms are a future iteration.
 */
export function Documents() {
  return (
    <Routes>
      <Route index element={<DocumentsList />} />
      <Route path=":personId" element={<PersonDocuments />} />
    </Routes>
  );
}

function DocumentsList() {
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const [q, setQ] = useState('');
  const list = usePeopleList(q);

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }

  const people = list.data ?? [];

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <header>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
            <FileImage className="h-3.5 w-3.5" />
            <span>Documents</span>
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-fg sm:text-3xl">
            Per-person attachments
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            Photograph driving licences, certs, or anything else inductees
            bring. Each attachment is stored against their record.
          </p>
        </header>

        <div className="relative mt-6">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-dim" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email..."
            className="block h-12 w-full rounded-token-lg border border-line-strong/20 bg-surface pl-11 pr-3 text-base text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>

        <div className="mt-4 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
          {list.isLoading ? (
            <SkeletonList rows={6} />
          ) : people.length === 0 ? (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title={q ? 'No matches' : 'No attendees yet'}
              description={
                q
                  ? `Nothing matches "${q}".`
                  : 'Import attendees first; they show up here.'
              }
            />
          ) : (
            <ul className="divide-y divide-divider/10">
              {people.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/documents/${p.id}`}
                    className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-2"
                  >
                    <PersonAvatar
                      fullName={p.full_name}
                      photoKey={p.current_photo_s3_key}
                      size="md"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-semibold text-fg">
                        {p.full_name}
                      </div>
                      <div className="truncate text-xs text-fg-muted">
                        {p.position ?? 'Position unknown'}
                        {p.email ? ` · ${p.email}` : ''}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-fg-dim" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}

interface DocumentRow {
  id: string;
  name: string;
  description: string | null;
  key: string;
  at: string;
  source: 'document' | 'form' | 'upload';
  content_type: string | null;
}

/** A file uploaded via onboarding forms/drafts/upload tasks (not the chain). */
interface OnboardingFileDoc {
  id: string;
  name: string;
  description: string | null;
  key: string;
  at: string;
  source: 'form' | 'upload';
  content_type: string | null;
}

function PersonDocuments() {
  const params = useParams<{ personId: string }>();
  const personId = params.personId ?? '';
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const personQ = usePerson(personId || null);
  const eventsQ = usePersonEvents(personId || null);
  const append = useAppendEvent(personId);
  const docIntent = useIntentKey();
  const [adding, setAdding] = useState(false);

  // Files uploaded through onboarding (forms, drafts, upload tasks) - only the
  // onboarding-access roles can read them, so don't even fetch otherwise.
  const canSeeOnboardingFiles = hasOnboardingAccess(user?.roles ?? []);
  const onboardingFilesQ = useQuery({
    queryKey: ['onboarding-file-documents', personId],
    queryFn: () =>
      api<{ documents: OnboardingFileDoc[] }>(
        `/api/admin/onboarding/people/${personId}/file-documents`,
      ),
    enabled: Boolean(personId) && canSeeOnboardingFiles,
  });

  const documents = useMemo<DocumentRow[]>(() => {
    const events = eventsQ.data ?? [];
    const chain: DocumentRow[] = events
      .filter(
        (e): e is MobilisationEvent =>
          e.event_type === 'DocumentAttached' && Boolean(e.document_s3_key),
      )
      .map((e) => {
        const p = e.payload as DocumentAttachedPayload;
        return {
          id: e.id,
          name: p.name,
          description: p.description ?? null,
          key: e.document_s3_key as string,
          at: e.created_at,
          source: 'document' as const,
          content_type: 'image/jpeg',
        };
      });
    const onboarding = onboardingFilesQ.data?.documents ?? [];
    return [...chain, ...onboarding].sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [eventsQ.data, onboardingFilesQ.data]);

  async function saveDocument(input: {
    name: string;
    description?: string;
    dataUrl: string;
  }) {
    try {
      await append.mutateAsync({
        event_type: 'DocumentAttached',
        payload: {
          name: input.name,
          ...(input.description ? { description: input.description } : {}),
        },
        document_jpeg: input.dataUrl,
        client_event_id: docIntent.key,
      } as Parameters<typeof append.mutateAsync>[0]);
      docIntent.renew();
      toast.success(`${input.name} saved`);
      setAdding(false);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not save the document',
      );
      throw err;
    }
  }

  const personName = personQ.data?.person.full_name ?? 'Person';

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <button
          type="button"
          onClick={() => navigate('/documents')}
          className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted transition-colors hover:text-fg"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All attendees
        </button>

        <header className="mt-3 flex items-center gap-3">
          {personQ.data ? (
            <PersonAvatar
              fullName={personName}
              photoKey={personQ.data.person.current_photo_s3_key}
              size="lg"
            />
          ) : null}
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-fg sm:text-3xl">
              {personName}
            </h1>
            <p className="text-sm text-fg-muted">
              {documents.length} document{documents.length === 1 ? '' : 's'} on file
            </p>
          </div>
        </header>

        <div className="mt-6 flex justify-end">
          <Button
            variant="primary"
            size="md"
            iconLeft={<FilePlus2 className="h-4 w-4" />}
            onClick={() => setAdding(true)}
          >
            Add document
          </Button>
        </div>

        <div className="mt-3 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
          {eventsQ.isLoading ? (
            <SkeletonList rows={3} />
          ) : documents.length === 0 ? (
            <EmptyState
              icon={<FileImage className="h-6 w-6" />}
              title="No documents yet"
              description="Photograph a driving licence, cert, or any other paper they bring in."
            />
          ) : (
            <ul className="divide-y divide-divider/10">
              {documents.map((d) => (
                <DocumentItem key={d.id} doc={d} personId={personId} />
              ))}
            </ul>
          )}
        </div>

        <AddDocumentModal
          open={adding}
          personName={personName}
          onClose={() => setAdding(false)}
          onSave={saveDocument}
        />
      </div>
    </main>
  );
}

function DocumentItem({ doc, personId }: { doc: DocumentRow; personId: string }) {
  // DocumentAttached docs stream from /api/documents; onboarding files (any
  // type) from the staff onboarding-file route.
  const href =
    doc.source === 'document'
      ? `/api/documents/${doc.key}`
      : `/api/admin/onboarding/people/${personId}/files/${doc.key}`;
  const isImage = (doc.content_type ?? '').startsWith('image/');
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-token-sm bg-surface-2 ring-1 ring-line/10 transition-colors hover:ring-accent/30"
        aria-label={`Open ${doc.name}`}
      >
        {isImage ? (
          <img
            src={href}
            alt={doc.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <FileText className="h-6 w-6 text-fg-dim" />
        )}
      </a>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-fg">{doc.name}</div>
        {doc.description ? (
          <p className="mt-0.5 whitespace-pre-wrap text-xs text-fg-muted">
            {doc.description}
          </p>
        ) : null}
        {doc.at ? (
          <div className="mt-1 text-[11px] tabular-nums text-fg-dim">
            Added {formatDateTime(new Date(doc.at))}
          </div>
        ) : null}
      </div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs font-medium text-accent hover:underline"
      >
        Open
      </a>
    </li>
  );
}
